import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  FormGroup,
} from "@mui/material";
import { useTranslation } from "react-i18next";

function fmt(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  if (!Number.isFinite(d.getTime())) return String(dt);
  return d.toLocaleString();
}

function toDatetimeLocalValue(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (!Number.isFinite(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function TenantAccessSection({
  tenant,
  setForm, // Added setForm prop
  disabled,
  onActivate,
  onDeactivate,
  onSetTrialExpiration,
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  const [trialEdit, setTrialEdit] = useState("");
  const [trialSaving, setTrialSaving] = useState(false);

  useEffect(() => {
    setTrialEdit(toDatetimeLocalValue(tenant.trial_expires_at));
  }, [tenant.trial_expires_at]);

  const trialExpired = useMemo(() => {
    if (tenant.account_type !== "test") return false;
    if (!tenant.trial_expires_at) return false;
    const exp = new Date(tenant.trial_expires_at);
    return Number.isFinite(exp.getTime()) && exp <= new Date();
  }, [tenant.account_type, tenant.trial_expires_at]);

  const canEditTrial = tenant.account_type === "test";

  const saveTrial = async () => {
    if (!trialEdit) return;
    setTrialSaving(true);
    try {
      const isoUtc = new Date(trialEdit).toISOString();
      await onSetTrialExpiration?.(isoUtc);
    } finally {
      setTrialSaving(false);
    }
  };

  // Generic toggle for boolean flags
  const handleToggle = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        {t("tenantAccess.title", "Prístup")}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Chip
          label={
            tenant.account_type === "test"
              ? t("tenantAccess.type.test", "Test")
              : t("tenantAccess.type.production", "Produkcia")
          }
          variant="outlined"
        />
        <Chip
          color={tenant.is_active ? "success" : "default"}
          label={tenant.is_active ? t("tenantAccess.active", "Aktívny") : t("tenantAccess.inactive", "Neaktívny")}
          variant={tenant.is_active ? "filled" : "outlined"}
        />
        {trialExpired ? (
          <Chip color="warning" label={t("tenantAccess.trialExpired", "Skúšobná doba vypršala")} />
        ) : null}
      </Stack>

      {/* --- NEW MODULE ACCESS SECTION --- */}
      <Box sx={{ mb: 3, p: 2, bgcolor: "action.hover", borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {t("tenantAccess.modulesTitle", "Povolené moduly a funkcie")}
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Switch
                checked={!!tenant.anon_validation}
                onChange={handleToggle("anon_validation")}
                disabled={disabled}
              />
            }
            label={t("tenantAccess.features.validation", "Validácia anonymizácie")}
          />
          <FormControlLabel
            sx={{ ml: 2 }}
            control={
              <Switch
                checked={!!tenant.anon_dicom}
                onChange={handleToggle("anon_dicom")}
                disabled={disabled}
              />
            }
            label={t("tenantAccess.features.dicom", "DICOM anonymizácia")}
          />
        </FormGroup>
        <Typography variant="caption" color="text.secondary">
          {t("tenantAccess.features.help", "Tieto nastavenia ovplyvňujú viditeľnosť sekcií v bočnom menu pre používateľov organizácie.")}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="body2" color="text.secondary">
        {t("tenantAccess.firstLogin", "Prvé prihlásenie")}: <b>{fmt(tenant.first_login_at)}</b>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("tenantAccess.trialExpires", "Skúšobná doba do")}: <b>{fmt(tenant.trial_expires_at)}</b>
      </Typography>

      {canEditTrial ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          <TextField
            type="datetime-local"
            size="small"
            fullWidth
            label={t("tenantAccess.actions.setTrialExpiresAt", "Nastaviť koniec testu")}
            value={trialEdit}
            onChange={(e) => setTrialEdit(e.target.value)}
            disabled={disabled || trialSaving}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            onClick={saveTrial}
            disabled={disabled || trialSaving || !trialEdit}
          >
            {trialSaving
              ? t("tenantAccess.actions.savingTrial", "Ukladám…")
              : t("tenantAccess.actions.saveTrial", "Uložiť")}
          </Button>
        </Stack>
      ) : null}

      <Typography variant="body2" color="text.secondary">
        {t("tenantAccess.deactivatedAt", "Deaktivované")}: <b>{fmt(tenant.deactivated_at)}</b>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("tenantAccess.deactivationReason", "Dôvod deaktivácie")}: <b>{tenant.deactivation_reason || "—"}</b>
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={onActivate} disabled={disabled || tenant.is_active}>
          {t("tenantAccess.actions.activate", "Aktivovať")}
        </Button>

        <TextField
          size="small"
          fullWidth
          label={t("tenantAccess.actions.deactivateReason", "Dôvod deaktivácie")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={disabled || !tenant.is_active}
        />

        <Button
          color="error"
          variant="contained"
          onClick={() => onDeactivate(reason)}
          disabled={disabled || !tenant.is_active}
        >
          {t("tenantAccess.actions.deactivate", "Deaktivovať")}
        </Button>
      </Stack>
    </Box>
  );
}